'use client';

/**
 * Runtime Shell App
 *
 * Single entry point that:
 * 1. Loads AppConfig from the configured source
 * 2. Creates routes dynamically via runtime-engine (Vite SPA)
 * 3. Wraps everything in providers (QueryClient, Theme, Preset, Config)
 */

import { useState, useEffect, useMemo, type ReactNode } from 'react';
import { BrowserRouter, RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { LiveblocksProvider } from '@liveblocks/react';
import { createClient } from '@liveblocks/client';
import {
  ThemeProvider,
  PresetProvider,
  Toaster,
  LoadingState,
  Typography,
  Stack,
  Card,
  Button,
} from '@org/design-system';
import {
  loadAppConfig,
  resolveConfigSource,
  ConfigProvider,
  RuntimeProviders,
  createAppRouter,
  type AppConfig,
  type ShadcnAppConfig,
} from '@org/runtime-engine';
import { ShadcnAppShell } from './ShadcnAppShell';
import { ThemeInjector } from './shadcn-renderer/ThemeInjector';
import { Toaster as SonnerToaster } from 'sonner';
import { ConfigBuilder } from './shadcn-renderer/config-builder/ConfigBuilder';
import { QueryPerformanceProvider } from './shadcn-renderer/observability/QueryPerformanceObserver';

// =============================================================================
// Subdomain-Based Tenant Resolution
// =============================================================================

/** Platform domain — subdomains are app IDs (e.g., dealflow.graphx.ai → "dealflow") */
const PLATFORM_DOMAIN = 'graphx.ai';

function resolveAppId(): { appId: string | null; brandedDomain?: string } {
  const params = new URLSearchParams(window.location.search);
  const queryApp = params.get('app');
  if (queryApp) return { appId: queryApp };

  const hostname = window.location.hostname;
  if (hostname === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
    return { appId: null };
  }
  if (hostname.endsWith('.pages.dev')) {
    return { appId: null };
  }

  if (hostname.endsWith(`.${PLATFORM_DOMAIN}`)) {
    const subdomain = hostname.slice(0, -(PLATFORM_DOMAIN.length + 1));
    if (subdomain && !subdomain.includes('.')) {
      return { appId: subdomain };
    }
  }

  if (hostname === PLATFORM_DOMAIN) {
    return { appId: null };
  }

  const parts = hostname.split('.');
  if (parts.length >= 2) {
    return { appId: null, brandedDomain: hostname };
  }

  return { appId: null };
}

function shouldUseShadcnShell(config: AppConfig): boolean {
  const params = new URLSearchParams(window.location.search);
  const migrationStatus = config.shadcnMigrationStatus ?? 'none';

  if (migrationStatus === 'migrated') {
    return true;
  }

  if (migrationStatus === 'preview') {
    return params.get('shadcn') === '1';
  }

  return false;
}


function CollaborationProvider({ children }: { children: ReactNode }) {
  const rawKey = import.meta.env.VITE_LIVEBLOCKS_PUBLIC_KEY as string | undefined;
  const isValidKey = typeof rawKey === 'string' && (rawKey.startsWith('pk_live_') || rawKey.startsWith('pk_test_'));
  const client = useMemo(
    () => isValidKey ? createClient({ publicApiKey: rawKey! }) : null,
    [isValidKey, rawKey],
  );

  if (!client) return <>{children}</>;
  return <LiveblocksProvider client={client}>{children}</LiveblocksProvider>;
}

// =============================================================================
// Query Client (singleton)
// =============================================================================

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

function ConfigError({ error }: { error: Error }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-8">
      <Card className="max-w-lg">
        <Stack gap={4} className="p-6 text-center">
          <Typography variant="h2" className="text-destructive">Configuration Error</Typography>
          <Typography color="muted">
            Failed to load the application configuration.
          </Typography>
          <pre className="rounded-lg bg-muted p-4 text-left overflow-auto">
            <Typography variant="small">{error.message}</Typography>
          </pre>
          <Typography variant="small" color="muted">
            Please verify the application configuration and try again.
          </Typography>
        </Stack>
      </Card>
    </div>
  );
}

function AppSwitcher({ config }: { config: AppConfig }) {
  const params = new URLSearchParams(window.location.search);
  const selectedApp = params.get('app') ?? config.id;
  const shadcnEnabled = params.get('shadcn') === 'true';

  const updateParam = (key: string, value?: string) => {
    const next = new URLSearchParams(window.location.search);
    if (!value) {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    window.location.search = next.toString();
  };

  return (
    <div className="fixed right-4 top-4 z-50 rounded-lg border border-border bg-card p-3 shadow-sm">
      <Stack gap={2}>
        <Typography variant="small" className="font-semibold">App switcher</Typography>
        <div className="flex gap-2">
          <Button
            variant={selectedApp === 'clientforce' ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => updateParam('app', 'clientforce')}
          >
            ClientForce
          </Button>
          <Button
            variant={selectedApp === 'eventhub' ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => updateParam('app', 'eventhub')}
          >
            EventHub
          </Button>
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-foreground">
          <input
            type="checkbox"
            className="h-4 w-4"
            checked={shadcnEnabled}
            onChange={event => updateParam('shadcn', event.target.checked ? 'true' : undefined)}
            aria-label="Enable shadcn shell"
          />
          Enable shadcn shell
        </label>
      </Stack>
    </div>
  );
}


function RoutedShell({ config }: { config: AppConfig }) {
  const useShadcnShell = config.shadcn === true;
  const router = useMemo(() => createAppRouter(config), [config]);

  if (useShadcnShell) {
    return (
      <BrowserRouter>
        <ShadcnAppShell config={config} />
      </BrowserRouter>
    );
  }

  return <RouterProvider router={router} />;
}

function RuntimeApp() {
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const { appId, brandedDomain } = resolveAppId();

    if (brandedDomain) {
      fetch(`/api/config/resolve-domain?domain=${encodeURIComponent(brandedDomain)}`)
        .then(resp => {
          if (!resp.ok) throw new Error(`Unknown domain: ${brandedDomain}`);
          return resp.json() as Promise<{ appId: string }>;
        })
        .then(({ appId: resolved }) => {
          return loadAppConfig({ type: 'pages', appId: resolved });
        })
        .then(loaded => {
          const params = new URLSearchParams(window.location.search);
          const shadcn = params.has('shadcn')
            ? params.get('shadcn') === 'true'
            : loaded.shadcn === true;
          setConfig({ ...loaded, shadcn });
        })
        .catch(setError);
      return;
    }

    const env = { ...(import.meta.env as Record<string, string | undefined>) };
    if (appId) {
      env.VITE_APP_ID = appId;
      env.VITE_APP_CONFIG_SOURCE = 'pages';
    }

    const source = resolveConfigSource(env);
    loadAppConfig(source)
      .then(loaded => {
        const params = new URLSearchParams(window.location.search);
        const shadcn = params.has('shadcn')
          ? params.get('shadcn') === 'true'
          : loaded.shadcn === true;
        setConfig({ ...loaded, shadcn });
      })
      .catch(setError);
  }, []);

  useEffect(() => {
    if (config) {
      document.title = config.name;
    }
  }, [config]);

  if (error) {
    return <ConfigError error={error} />;
  }

  if (!config) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingState message="Loading application..." />
      </div>
    );
  }


  const resolvedConfig: AppConfig | ShadcnAppConfig = config.shadcn === true
    ? (config as ShadcnAppConfig)
    : config;

  return (
    <QueryPerformanceProvider client={queryClient} appId={resolvedConfig.id}>
      <ThemeProvider defaultTheme={{ mode: 'light' }}>
        <PresetProvider
          presetId={resolvedConfig.presetId}
          fallbackPresetId={resolvedConfig.fallbackPresetId}
        >
          <ThemeInjector theme={config.theme} />
          <ConfigProvider config={resolvedConfig}>
            <CollaborationProvider>
              <RuntimeProviders config={resolvedConfig}>
                <RoutedShell config={resolvedConfig} />
                {!resolvedConfig.shadcn && <AppSwitcher config={resolvedConfig} />}
                <Toaster />
                <SonnerToaster richColors position="top-right" />
              </RuntimeProviders>
            </CollaborationProvider>
          </ConfigProvider>
        </PresetProvider>
      </ThemeProvider>
    </QueryPerformanceProvider>
  );
}


export default function App() {
  const params = new URLSearchParams(window.location.search);
  const isConfigBuilderMode = params.get('builder') === '1';

  if (isConfigBuilderMode) {
    return <ConfigBuilder initialApiBaseUrl={params.get('apiUrl') ?? ''} />;
  }

  return <RuntimeApp />;
}
